import React, { useState } from "react";
import { CloudDownload, ImageOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { prefetchPhotos } from "@/lib/offlinePrefetch";

const fmt = (s) => { try { return new Date(s).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch (_) { return ""; } };

// Lists photos that exist on the server but are not stored on this device; load all or single ones.
export default function MissingPhotosDialog({ open, onClose, missing }) {
  const [busy, setBusy] = useState(null); // "all" | photo id
  const load = async (files, key) => {
    if (!navigator.onLine) return toast.error("Nachladen geht nur mit Internetverbindung");
    setBusy(key);
    const n = await prefetchPhotos(files).catch(() => 0);
    setBusy(null);
    if (n === files.length) toast.success(n === 1 ? "Foto ist jetzt offline gespeichert" : `${n} Fotos sind jetzt offline gespeichert`);
    else toast.error(`${n} von ${files.length} Fotos geladen – bitte erneut versuchen`);
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="missing-photos-dialog">
        <DialogHeader>
          <DialogTitle>Fehlende Fotos</DialogTitle>
          <DialogDescription>Diese Fotos liegen auf dem Server, sind aber noch nicht auf diesem Gerät gespeichert.</DialogDescription>
        </DialogHeader>
        {missing.length === 0 ? (
          <p className="text-sm text-green-700 py-4" data-testid="missing-photos-empty">Alle Fotos sind auf diesem Gerät gespeichert.</p>
        ) : (
          <>
            <Button onClick={() => load(missing, "all")} disabled={!!busy} data-testid="missing-photos-load-all" className="w-full">
              {busy === "all" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CloudDownload className="h-4 w-4 mr-2" />}
              Alle {missing.length} nachladen
            </Button>
            <ul className="max-h-[50vh] overflow-y-auto divide-y divide-slate-100 -mx-1">
              {missing.map((f) => (
                <li key={f.id} className="flex items-center gap-3 px-1 py-2.5" data-testid={`missing-photo-${f.id}`}>
                  <ImageOff className="h-5 w-5 text-amber-600 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-800 truncate">{f.description || f.name || "Foto"}</p>
                    <p className="text-xs text-slate-500">{fmt(f.created_at)}</p>
                  </div>
                  <Button size="sm" variant="outline" disabled={!!busy} onClick={() => load([f], f.id)} data-testid={`missing-photo-load-${f.id}`}>
                    {busy === f.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Laden"}
                  </Button>
                </li>
              ))}
            </ul>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
